import { Alert } from '../components/misc';
import { Button } from '../components/button';
import { EventTimeline, type EventTimelineItem } from './event-timeline';
import type { ReactNode } from 'react';

export interface QuarantineHistoryPanelProps {
  /** Caller-owned evidence scope/binding summary; never interpreted as authority by this renderer. */
  summary?: ReactNode;
  /** Rich admin evidence adapter; recipient clients keep the bounded event presentation. */
  children?: ReactNode;
  title: string;
  partialNotice: string;
  unavailableNotice: string;
  emptyNotice: string;
  truncatedNotice: string;
  timelineLabel: string;
  events: readonly EventTimelineItem[];
  status?: 'recorded' | 'unavailable';
  loading?: boolean;
  loadingLabel: string;
  error?: string;
  retryLabel: string;
  onRetry?: () => void;
  firstPageLabel: string;
  nextLabel: string;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  showTruncatedNotice?: boolean;
  onFirstPage?: () => void;
  onNext?: () => void;
}

/** Presentational quarantine history shared by admin and self-service clients. */
export function QuarantineHistoryPanel({
  summary,
  children,
  title,
  partialNotice,
  unavailableNotice,
  emptyNotice,
  truncatedNotice,
  timelineLabel,
  events,
  status,
  loading = false,
  loadingLabel,
  error,
  retryLabel,
  onRetry,
  firstPageLabel,
  nextLabel,
  canGoPrevious = false,
  canGoNext = false,
  showTruncatedNotice = false,
  onFirstPage,
  onNext,
}: QuarantineHistoryPanelProps) {
  const pending = loading || (status === undefined && !error);
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">{title}</h2>
      {summary}
      <Alert variant="warning">{partialNotice}</Alert>
      {pending ? <p role="status" className="text-sm text-muted-foreground">{loadingLabel}</p> : error ? (
        <div className="flex flex-col gap-2" role="alert">
          <p className="text-sm text-destructive">{error}</p>
          {onRetry && <Button type="button" variant="outline" size="sm" onClick={onRetry}>{retryLabel}</Button>}
        </div>
      ) : status === 'unavailable' ? (
        <p className="text-sm text-muted-foreground">{unavailableNotice}</p>
      ) : status === 'recorded' && children !== undefined ? children : status === 'recorded' && events.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyNotice}</p>
      ) : status === 'recorded' ? (
        <EventTimeline items={events} label={timelineLabel} />
      ) : null}
      {(pending || error) && canGoPrevious && onFirstPage && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onFirstPage}>{firstPageLabel}</Button>
        </div>
      )}
      {!pending && !error && status === 'recorded' && (
        <div className="flex flex-wrap gap-2">
          {canGoPrevious && onFirstPage && <Button type="button" variant="outline" size="sm" onClick={onFirstPage}>{firstPageLabel}</Button>}
          {canGoNext && onNext && <Button type="button" variant="outline" size="sm" onClick={onNext}>{nextLabel}</Button>}
          {showTruncatedNotice && <p className="self-center text-sm text-muted-foreground">{truncatedNotice}</p>}
        </div>
      )}
    </section>
  );
}
